import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangProvider } from "../contexts/LangContext";
import { DonatePage } from "../pages/DonatePage";
import { buildCompleteHead } from "../utils/seo";

const allowedLanguages = ["oz", "uz", "ru", "en"];

export const Route = createFileRoute("/$lang/donate")({
  head: ({ params }) => {
    const currentPath = `/${params.lang}/donate`;
    const canonicalUrl = `https://lotin.uz${currentPath}`;
    const seoData = buildCompleteHead(
      params.lang as "oz" | "uz" | "ru",
      "home",
      canonicalUrl,
      currentPath,
      currentPath,
    );

    return {
      meta: [{ title: seoData.title }, ...seoData.meta],
      links: seoData.links,
    };
  },
  // Cast as any to satisfy older `beforeLoad` typing that expects `never`
  beforeLoad: ((ctx: { params: { lang: string } }) => {
    const { params } = ctx;
    if (!allowedLanguages.includes(params.lang)) {
      throw redirect({ to: "/donate" });
    }
    if (params.lang === "oz") {
      throw redirect({ to: "/donate" });
    }
  }) as any,
  component: Donate,
});

function Donate() {
  const { lang } = useParams({ from: "/$lang/donate" });
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap: Record<string, string> = {
      oz: "uz-Latn",
      uz: "uz-Cyrl",
      ru: "ru",
      en: "en-US",
    };
    document.documentElement.lang = langMap[lang] || "uz-Latn";
    // Guard for SSR: only call changeLanguage if it exists
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }

    const titleMap: Record<string, string> = {
      oz: "Homiylik — Lotin.uz",
      uz: "Хомийлик — Lotin.uz",
      ru: "Пожертвования — Lotin.uz",
      en: "Donate — Lotin.uz",
    };
    document.title = titleMap[lang] || titleMap.oz;
  }, [lang, i18n]);

  const langMapForContext: Record<string, string> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  return (
    <LangProvider lang={langMapForContext[lang] || "uz-Latn"}>
      <DonatePage lang={langMapForContext[lang] || "uz-Latn"} />
    </LangProvider>
  );
}
